import { Store } from 'redux';
import { WidgetType } from 'widgets/WidgetType';
import LayoutShape, { LayoutContainerShape } from 'layout/LayoutShape';
import { RootComponentExposedProps } from './RootComponent';
import Services from 'store/Services';

interface LayoutRendererContainerComponent {
    type: 'row' | 'column' | 'stack';
    content: LayoutRendererComponent[];
    isClosable: boolean;
    width?: number;
    height?: number;
    activeItemIndex?: number;
}

export interface LayoutRendererReactComponent {
    type: 'component' | 'react-component';
    component: WidgetType;
    title: string;
    isClosable: boolean;
    props: RootComponentExposedProps;
}

type LayoutRendererComponent =
    | LayoutRendererContainerComponent
    | LayoutRendererReactComponent;

export default LayoutRendererComponent;

export function fromLayoutShape(
    layoutShape: LayoutShape,
    store: Store,
    services: Services
): LayoutRendererComponent {
    if (layoutShape.type === 'component') {
        return {
            type: 'react-component',
            component: services.widgetsService.getStrategyType(
                layoutShape.componentType
            ),
            title: services.widgetsService.getWidgetTitle(
                layoutShape.componentType
            ),
            isClosable: true,
            props: {
                componentId: layoutShape.componentId,
                componentType: layoutShape.componentType,
                store,
                services,
            },
        };
    } else {
        return {
            type: layoutShape.type,
            content: (layoutShape.content || []).map((s) =>
                fromLayoutShape(s, store, services)
            ),
            isClosable: true,
            width: layoutShape.width,
            height: layoutShape.height,
            ...(layoutShape.type === 'stack'
                ? { activeItemIndex: layoutShape.activeItemIndex || 0 }
                : {}),
        };
    }
}

export function toLayoutShape(component: LayoutRendererComponent): LayoutShape {
    if (
        component.type === 'component' ||
        component.type === 'react-component'
    ) {
        return {
            type: 'component',
            componentType: component.props.componentType,
            componentId: (component as LayoutRendererReactComponent).props
                .componentId,
        };
    } else {
        const ret = {
            type: component.type,
            content: (
                (component as LayoutRendererContainerComponent).content || []
            ).map(toLayoutShape),
            width: (component as LayoutRendererContainerComponent).width,
            height: (component as LayoutRendererContainerComponent).height,
        };

        // Work around for https://github.com/golden-layout/golden-layout/issues/418 as golden layout can't be bothered to update their NPM package...
        if (component.type === 'stack') {
            const stack = component as LayoutRendererContainerComponent;
            let activeItemIndex = stack.activeItemIndex ?? 0;
            if (activeItemIndex > stack.content.length - 1) {
                activeItemIndex = stack.content.length - 1;
            }
            (ret as LayoutContainerShape).activeItemIndex = activeItemIndex;
        }
        return ret;
    }
}
